import { assert } from '@sama/js-core';
import { Ray, Vector2, Vector4 } from 'three';
import { SettingState } from '../../../cross-team-shared/state/setting-state';
import { Point3D } from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { getCameraData } from '../calibration-utils';
import {
  AbstractProjectionUtils,
  ProjectTo2DOptions,
} from './abstract-projection-utils';

export class BrownConradyProjectionUtils extends AbstractProjectionUtils {
  private readonly principlePoint = new Vector2();
  private readonly focalLength = new Vector2();
  private readonly position = new Vector4();

  // eslint-disable-next-line max-lines-per-function -- single function to keep it similar to the original GLSL code
  projectTo2D(
    point: Point3D,
    settingState: SettingState,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    const { distortion, focalLength, principlePoint } = getCameraData(
      state.calibrationData[options.outputName],
      state.render.calibrationOptions[options.outputName],
      state.render.frameNumber,
    );
    this.principlePoint.fromArray(principlePoint);
    this.focalLength.fromArray(focalLength);
    this.position.fromArray([...point, 1]);

    assert(
      distortion.k && distortion.p && distortion.model === 'brown_conrady',
      'p and k coefficients are required for brown conrady projection',
    );
    const k = distortion.k.slice(0, 2);
    const p = distortion.p.slice(0, 2);

    const imageSize = this.getImageSize(state, options.outputName);

    this.updateProjectionCamera(state, settingState, options.outputName);

    const pinholePosition = this.localToClipSpace(this.position, options);

    const undistortedNDC = new Vector2(
      pinholePosition.x / pinholePosition.w,
      pinholePosition.y / pinholePosition.w,
    );

    const undistortedPixelCoordinate = undistortedNDC
      .multiply(imageSize)
      .add(imageSize)
      .divideScalar(2);

    const normalizedCoordinate = undistortedPixelCoordinate
      .sub(this.principlePoint)
      .divide(this.focalLength);

    const k1 = k[0];
    const k2 = k[1];

    const p1 = p[0];
    const p2 = p[1];

    const s = normalizedCoordinate.x;
    const t = normalizedCoordinate.y;

    const r2 = s * s + t * t;
    const d = 1 + k1 * r2 + k2 * r2 * r2;
    const u = s * d + 2 * p1 * s * t + p2 * (r2 + 2 * s * s);
    const v = t * d + p1 * (r2 + 2 * t * t) + 2 * p2 * s * t;

    const distortedNormalizedCoordinate = new Vector2(u, v);
    const distortedPixelCoordinate = distortedNormalizedCoordinate
      .multiply(this.focalLength)
      .add(this.principlePoint);

    return {
      position: distortedPixelCoordinate.toArray(),
      isInsideFrustum: this.isWithinFrustum(
        distortedPixelCoordinate,
        imageSize,
        pinholePosition.w,
        pinholePosition.z,
      ),
    };
  }

  projectTo3D(): Ray {
    throw new Error('3D projection not supported for Brown Conrady model');
  }
}
